<script setup>
import AlertActionsCell from "@frontJs/components/tables/cells/AlertActionsCell.vue";
import TenantAppointmentCell from "@frontJs/components/tables/cells/projectManagement/apikom/tenantReports/TenantAppointmentCell.vue";
import CustomerTaskDetailsCell from "@frontJs/components/tables/cells/projectManagement/tasks/CustomerTaskDetailsCell.vue";
import LoadingSpinner from "@js/components/LoadingSpinner.vue";
import ExportButton from "@js/components/buttons/ExportButton.vue";
import DataTableLayout from "@js/components/tables/DataTableLayout.vue";
import CrossIconCell from "@js/components/tables/cells/CrossIconCell.vue";
import LinkCellV2 from "@js/components/tables/cells/LinkCellV2.vue";
import PillCell from "@js/components/tables/cells/PillCell.vue";
import TaskPriorityCell from "@js/components/tables/cells/TaskPriorityCell.vue";
import TextCellV2 from "@js/components/tables/cells/TextCellV2.vue";
import { useDataTable } from "@js/utils/dataTable";
import { formatDate } from "@js/utils/formatter";
import { useRoute } from "@js/utils/route";
import { createColumnHelper } from "@tanstack/vue-table";
import { h } from "vue";
import { useI18n } from "vue-i18n";

const {
  group = undefined,
  type = undefined,
  ongoing,
  processed,
} = defineProps({
  group: {
    required: false,
    type: String,
  },
  type: {
    required: false,
    type: String,
  },
  ongoing: {
    required: false,
    type: Boolean,
  },
  processed: {
    required: false,
    type: Boolean,
  },
});

const { t } = useI18n();

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
  loading,
  table,
  count,
  globalFilter,
  setGlobalFilter,
  availableFilters,
  filters,
  setFilters,
  updateData,
  selectionIds,
  exportData,
} = useDataTable({
  columns: [
    columnHelper.accessor(() => null, {
      id: "alerts.group",
      header: () => t("label.group"),
      cell: (props) =>
        h(PillCell, {
          pillValue: props.row.original.group.label,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "alerts.type",
      header: () => t("label.type"),
      cell: (props) =>
        h(PillCell, {
          pillValue: props.row.original.type.label,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "tenants.name",
      header: () => t("label.customer"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.tenant.name,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "alerts.message",
      header: () => t("label.message"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.message,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "alerts.data->appointment_last_login_at",
      header: () => t("label.lastLoginDate"),
      cell: (props) =>
        props.row.original.data.appointmentLastLoginAt
          ? h(TextCellV2, {
            text: props.row.original.data.appointmentLastLoginAt,
          })
          : null,
    }),
    columnHelper.accessor(() => null, {
      id: "alerts.data->tenant_appointment_frequency",
      header: () => t("label.appointmentFrequency"),
      cell: (props) =>
        props.row.original.data.tenantAppointmentFrequency
          ? h(PillCell, {
              pillValue: t(
                "label." + props.row.original.data.tenantAppointmentFrequency,
              ),
            })
          : undefined,
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "alerts.data->last_appointment",
      header: () => t("label.lastAppointment"),
      cell: (props) =>
        props.row.original.data.lastAppointment
          ? h(TenantAppointmentCell, {
              appointment: props.row.original.data.lastAppointment,
            })
          : undefined,
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "alerts.data->appointment_mail_request_sent_at",
      header: () => t("label.email"),
      cell: (props) =>
        props.row.original.data.appointmentMailRequestSentAt
          ? h(TextCellV2, {
              text: props.row.original.data.appointmentMailRequestSentAt,
            })
          : undefined,
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "alerts.data->seo_backlink_date",
      header: () => t("label.date"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.data.seoBacklinkDate,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "alerts.data->seo_backlink_source_url",
      header: () => t("label.sourceUrl"),
      cell: (props) =>
        h(LinkCellV2, {
          text: new URL(props.row.original.data.seoBacklinkSourceUrl).hostname,
          href: props.row.original.data.seoBacklinkSourceUrl,
          maxWidth: 250,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "alerts.data->seo_backlink_checked_at",
      header: () => t("label.checkedAt"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.data.seoBacklinkCheckedAt,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "alerts.data->tenant_task_full_name",
      header: () => t("label.name"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.data.tenantTaskFullName,
        }),
    }),
    columnHelper.display({
      id: "alerts.data->tenant_task_details",
      header: t("label.details"),
      cell: (props) =>
        h(CustomerTaskDetailsCell, {
          row: {
            original: {
              description: props.row.original.data.tenantTaskDescription,
            },
          },
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "alerts.data->tenant_task_priority",
      header: () => t("label.priority"),
      cell: (props) =>
        h(TaskPriorityCell, {
          data: {
            priority: props.row.original.data.tenantTaskPriority,
          },
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "alerts.data->tenant_task_created_at",
      header: () => t("label.createdDate"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.data.tenantTaskCreatedAt,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "alerts.data->tenant_task_done_at",
      header: () => t("label.customerValidationDate"),
      cell: (props) =>
        props.row.original.data.tenantTaskDoneAt
          ? h(TextCellV2, {
              text: props.row.original.data.tenantTaskDoneAt,
            })
          : undefined,
    }),
    columnHelper.accessor(() => null, {
      id: "alerts.createdAt",
      header: () => t("label.createdAt"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.createdAt,
        }),
    }),
    columnHelper.display({
      id: "actions",
      header: t("label.actions"),
      cell: (props) =>
        h(AlertActionsCell, {
          row: props.row,
          callback: updateData,
          group,
          type,
        }),
      width: 0,
    }),
  ],
  url: route("front.postAlerts"),
  exportUrl: route("front.postExportAlerts"),
  data: {
    group,
    type,
    ongoing,
    processed,
  },
  columnVisibility: {
    "alerts.group": !group && !type,
    "alerts.type": !type,
    "alerts.data->appointment_last_login_at": type === "appointment_to_make",
    "alerts.data->tenant_appointment_frequency": type === "appointment_to_make",
    "alerts.data->last_appointment": type === "appointment_to_make",
    "alerts.data->appointment_mail_request_sent_at":
      type === "appointment_to_make",
    "alerts.data->seo_backlink_date": type === "dead_netlinking_link",
    "alerts.data->seo_backlink_source_url": type === "dead_netlinking_link",
    "alerts.data->seo_backlink_checked_at": type === "dead_netlinking_link",
    "alerts.data->tenant_task_full_name": group === "tasks",
    "alerts.data->tenant_task_details": group === "tasks",
    "alerts.data->tenant_task_priority": group === "tasks",
    "alerts.data->tenant_task_created_at": group === "tasks",
    "alerts.data->tenant_task_done_at": group === "tasks",
  },
  columnPinning: {
    right: ["actions"],
  },
  selectionIdKey: "id",
  exportSelectionIdKey: "alerts.id",
});
</script>

<template>
  <LoadingSpinner v-if="loading" />

  <DataTableLayout
    v-else
    :table="table"
    :count="count"
    :global-filter="globalFilter"
    :set-global-filter="setGlobalFilter"
    :available-filters="availableFilters"
    :filters="filters"
    :set-filters="setFilters"
  >
    <template #actions>
      <ExportButton
        :csv-export-callback="() => exportData('csv')"
        :xlsx-export-callback="() => exportData('xlsx')"
        :disabled="selectionIds.length === 0"
      />
    </template>
  </DataTableLayout>
</template>
